import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation, useOutletContext, useParams } from 'react-router';
import { type FormId, type Page } from '../../../common/constants/pathInterfaces.js';
import { paths } from '../../../common/constants/pathVariables.js';
import { backMsg, clearFormMsg, savingDraftMsg, t } from '../../../common/i18n/index.js';
import { OpenFormBar } from '../OpenFormComponents/OpenFormBar.js';
import { OpenFormButton } from '../OpenFormComponents/OpenFormButton.js';
import { OpenFormGrid, OpenFormGridCol, OpenFormGridRow } from '../OpenFormComponents/OpenFormGrid.js';
import { useEventListener } from '../OpenFormHooks/useEventListener.js';
import { useOpenForm } from '../OpenFormHooks/useOpenForm.js';
import { type OpenFormGotoProps, useOpenFormGoto } from '../OpenFormHooks/useOpenFormGoto.js';
import { type OpenFormOutlet } from '../OpenFormLayout/OpenFormLayoutOutlet.js';
import { useOpenFormAsync, useOpenFormState } from '../OpenFormProvider.js';
import { OpenFormViewSection } from './OpenFormViewSection.js';
import { OpenFormViewSummary } from './OpenFormViewSummary.js';

export const OpenFormViewLayout = () => {
  const { list = paths.OPENFORM_LIST_FORM } = (useLocation().state ?? {}) as OpenFormGotoProps;
  const { reset } = useFormContext();
  const { preview } = useOutletContext<Required<OpenFormOutlet>>();
  const { answers, disabled, form, page } = useOpenFormState();
  const params = useParams<FormId & Page>();
  const async = useOpenFormAsync();
  const goto = useOpenFormGoto();
  const ready = useOpenForm(preview, params.formId!);
  const snapshot = useRef({ cooldown: 0, debounce: 0, promise: Promise.resolve() as Promise<unknown> });
  const breadcrumbs = useMemo(() => Array.from({ length: (form?.sections.length || -1) + 1 }, (_, i) => i + 1), [form]);

  const back = useCallback(() => {
    form?.referenceNumber && form.postDraft().execute({ disable: true, message: t.R0YI(savingDraftMsg) });
    goto({ list });
  }, [list, form, goto]);

  const clear = useCallback(() => {
    async.cache.clear();
    async.actions.answers(undefined);
    reset({});
    goto({ page: 1 });
  }, [reset, async, goto]);

  // Navigate to OpenFormState.page
  useEffect(() => {
    form && params.page !== String(page) && goto({ page }, { list, referenceNumber: form.referenceNumber });
  }, [list, form, page, params, async, goto]);

  useEventListener(
    'snapshot',
    useCallback(
      ({ key, value }: OpenFormSnapshotEvent, signal: AbortSignal) => {
        if (!preview && async.draft.has(key) && async.flags.has('draft')) {
          snapshot.current.promise.then(() => {
            clearTimeout(snapshot.current.debounce);
            snapshot.current.debounce = self.setTimeout(
              () => {
                if (key === async.state.form?.reference && async.draft.has(key) && async.flags.has('draft')) {
                  snapshot.current.promise = async.state.form
                    .postDraft(value!)
                    .settled(() => (snapshot.current.cooldown = Date.now() + 5000))
                    .promise({ disable: false, message: t.R0YI(savingDraftMsg) }, signal);
                }
              },
              Math.max(snapshot.current.cooldown - Date.now(), 2000)
            );
          });
        }
      },
      [preview, async, snapshot]
    )
  );

  return (
    <OpenFormBar
      leftFunc={preview ? undefined : back}
      leftIcon="arrow-left"
      leftText={t.D1E9(backMsg)}
      rightFunc={answers.size ? clear : undefined}
      rightIcon="delete"
      rightText={t.JQ1V(clearFormMsg)}
    >
      {!ready ? null : (
        <OpenFormGrid className="of-openform__view">
          <OpenFormGridRow className="of-openform__view__breadcrumbs">
            <OpenFormGridCol className="of-openform__view__breadcrumbs__grid" colWidth={9}>
              {breadcrumbs.map(num => (
                <OpenFormButton
                  className={classNames({ ['breadcrumb--incomplete']: num > page })}
                  disabled={disabled}
                  color="primary"
                  size="l"
                  text={num}
                  key={num}
                />
              ))}
            </OpenFormGridCol>
          </OpenFormGridRow>
          {!form ? null : form.sections.length >= page ? (
            <OpenFormViewSection answers={answers} disabled={disabled} form={form} goto={goto} page={page} />
          ) : (
            <OpenFormViewSummary answers={answers} disabled={disabled} form={form} goto={goto} preview={preview} />
          )}
        </OpenFormGrid>
      )}
    </OpenFormBar>
  );
};
